'use client';

import type { PressEvent } from 'react-aria';
import { useFocusRing, usePress } from 'react-aria';
import type { ListState, Node } from 'react-stately';
import { isAppleDevice, mergeProps, useId } from '@react-aria/utils';

export function useCarouselItemCheckbox<T>(state: ListState<T>, item: Node<T>) {
  const id = useId();
  const manager = state.selectionManager;
  const isSelected = manager.isSelected(item.key);
  const isDisabled = !manager.canSelectItem(item.key);

  const { isPressed, pressProps } = usePress({
    onPressStart: (e) => {
      // Keyboard-based non-contiguous selection
      if (e.pointerType === 'keyboard' && isNonContiguousSelectionModifier(e)) {
        return manager.toggleSelection(item.key);
      }

      // Shift key extends existing selection in any mode
      if (e.shiftKey) {
        return manager.extendSelection(item.key);
      }

      // Single selection mode
      if (manager.selectionMode === 'single') {
        return manager.isSelected(item.key)
          ? manager.toggleSelection(item.key)
          : manager.replaceSelection(item.key);
      }

      // Default behavior
      return manager.selectionBehavior === 'toggle'
        ? manager.toggleSelection(item.key)
        : manager.replaceSelection(item.key);
    },
  });

  const { focusProps, isFocusVisible } = useFocusRing();

  return {
    isFocusVisible,
    isSelected,
    isDisabled,
    isPressed,
    checkboxProps: mergeProps(
      {
        tabIndex: 0,
        id,
        checked: isSelected,
      },
      pressProps,
      focusProps
    ),
  };
}

function isNonContiguousSelectionModifier(e: KeyboardEvent | PressEvent) {
  return isAppleDevice() ? e.altKey : e.ctrlKey;
}
